import { SETTINGS, ME, USER, SUBMISSIONS, LOCATION, } from "../shared/tagTypes";
import { baseApi } from "./base";

export const meApi = baseApi
  .enhanceEndpoints({
    addTagTypes: [ME, LOCATION, SETTINGS, USER, SUBMISSIONS],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getMe: builder.query({
        query: () => ({
          url: "/me/v2/profile",
        }),
        providesTags: [ME],
      }),
      updateMyProfile: builder.mutation({
        query: (payload) => ({
          url: "/me/profile",
          method: "POST",
          body: payload.body,
        }),
        invalidatesTags: [ME, USER],
      }),
      getMyLocation: builder.query({
        query: (payload: { latitude: number, longitude: number }) => ({
          url: `/me/my-location?latitude=${payload.latitude}&longitude=${payload.longitude}`,
        }),
        providesTags: [LOCATION],
      }),
      uploadMyPhoto: builder.mutation({
        query: (payload) => ({
          url: "/me/photograph",
          method: "POST",
          body: payload,
        }),
        invalidatesTags: [ME, USER],
      }),
      updateMyPhoto: builder.mutation({
        query: (payload) => ({
          url: "/me/photograph/update",
          method: "POST",
          body: payload,
        }),
        invalidatesTags: [ME, USER],
      }),
      deleteMyPhoto: builder.mutation<void, void>({
        query: () => ({
          url: "/me/photograph",
          method: "DELETE",
        }),
        invalidatesTags: [ME, USER],
      }),
      submitSurvey: builder.mutation({
        query: (payload) => ({
          url: `/surveys/${payload.surveyId}/submissions`,
          method: "POST",
          body: payload.answers
        }),
        invalidatesTags: [SUBMISSIONS],
      }),
      resubmitSurvey: builder.mutation({
        query: (payload) => ({
          url: `/surveys/${payload.surveyId}/submissions/${payload.submissionId}/resubmit`,
          method: "POST",
          body: payload.answers
        }),
        invalidatesTags: [SUBMISSIONS],
      }),
      saveMyFcmIdToken: builder.mutation({
        query: (payload) => ({
          url: "/me/my-devices",
          method: "POST",
          body: payload.body,
        }),
      }),
      changeMyPassword: builder.mutation({
        query: (payload) => ({
          url: "/me/password",
          method: "POST",
          body: payload,
        }),
      }),
      updateMyBasicInfo: builder.mutation({
        query: (payload) => ({
          url: "/me/profile/basic-info",
          method: "PATCH",
          body: payload.body,
        }),
        invalidatesTags: [ME, USER],
      }),
      updateMyContactInfo: builder.mutation({
        query: (payload) => ({
          url: "/me/profile/contact-info",
          method: "PATCH",
          body: payload.body,
        }),
        invalidatesTags: [ME, USER],
      }),
      updateMyWorkEducation: builder.mutation({
        query: (payload) => ({
          url: "/me/profile/work-education",
          method: "PATCH",
          body: payload.body,
        }),
        invalidatesTags: [ME, USER],
      }),
      updateMyReligiousInfo: builder.mutation({
        query: (payload) => ({
          url: "/me/profile/religious-info",
          method: "PATCH",
          body: payload.body,
        }),
        invalidatesTags: [ME, USER],
      }),
      updateMyPersonalInfo: builder.mutation({
        query: (payload) => ({
          url: "/me/profile/personal-info",
          method: "PATCH",
          body: payload.body,
        }),
        invalidatesTags: [ME, USER],
      }),
      getMySubmissions: builder.query({
        query: (payload) => ({
          url: "/v2/me/my-submissions",
          params: {
            ...(payload.pageSize && {
              pageSize: payload.pageSize,
            }),
            ...(payload.submittedBefore && {
              submittedBefore: payload.submittedBefore,
            }),
            ...(payload.language && {
              language: payload.language,
            }),
          },
        }),
        providesTags: [USER],
      }),
      getMyProfileCompletionStatus: builder.query<any, void>({
        query: () => ({
          url: "/me/my-profile-completion"
        }),
        providesTags: [ME],
      }),
      getMyPendingRejectedSurveys: builder.query<any, void>({
        query: () => ({
          url: "/me/my-pending-rejected-surveys"
        }),
        providesTags: [ME],
      }),
    }),

    overrideExisting: false,
  });

export const {
  useGetMeQuery,
  useLazyGetMeQuery,
  useGetMyLocationQuery,
  useLazyGetMyLocationQuery,
  useUploadMyPhotoMutation,
  useUpdateMyPhotoMutation,
  useDeleteMyPhotoMutation,
  useSubmitSurveyMutation,
  useResubmitSurveyMutation,
  useSaveMyFcmIdTokenMutation,
  useChangeMyPasswordMutation,
  useUpdateMyBasicInfoMutation,
  useUpdateMyContactInfoMutation,
  useUpdateMyPersonalInfoMutation,
  useUpdateMyReligiousInfoMutation,
  useUpdateMyWorkEducationMutation,
  useGetMySubmissionsQuery,
  useLazyGetMySubmissionsQuery,
  useGetMyProfileCompletionStatusQuery,
  useLazyGetMyProfileCompletionStatusQuery,
  useGetMyPendingRejectedSurveysQuery,
  useLazyGetMyPendingRejectedSurveysQuery,
  useUpdateMyProfileMutation
} = meApi;
